import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const actions = 'button, .btn, .btn-sm, a[href], summary, [role="button"], input[type="submit"], input[type="button"]';
const iconHelp = {
  eye: 'View details', edit: 'Edit details', pencil: 'Edit details', 'pencil-square-o': 'Edit details',
  trash: 'Delete', 'trash-o': 'Delete', plus: 'Add', 'plus-circle': 'Add',
  print: 'Print', save: 'Save changes', check: 'Confirm', 'check-circle': 'Confirm',
  times: 'Close', 'times-circle': 'Cancel', close: 'Close', refresh: 'Refresh',
  search: 'Search', filter: 'Filter results', scan: 'Scan barcode', qrcode: 'Scan barcode',
  camera: 'Take photo', upload: 'Upload file', download: 'Download', copy: 'Copy',
  'arrow-left': 'Go back', 'arrow-right': 'Continue', 'chevron-left': 'Previous',
  arrowLeft: 'Go back', arrowRight: 'Continue',
  'chevron-right': 'Next', 'caret-up': 'Move up', 'caret-down': 'Move down',
  'chevron-down': 'Show options', 'chevron-up': 'Hide options',
  'paper-plane': 'Submit purchase order', link: 'Associate invoice', scale: 'Calculate weight',
  shipping: 'Go to shipment', audit: 'View audit', settings: 'Settings', calendar: 'Schedule maintenance',
  collapse: 'Collapse items', 'calendar-plus-o': 'Schedule maintenance', play: 'Start', pause: 'Pause',
  'info-circle': 'More information', info: 'More information',
  'question-circle': 'Help', 'sign-out': 'Log out', logout: 'Log out', menu: 'Open navigation',
  expand: 'Expand items', compress: 'Collapse items',
  'balance-scale': 'Calculate weight',
  'auto-calc': 'Calculate weight', 'bolt': 'Calculate', 'reply': 'Return',
};
const helpers = {
  clear: 'Clear', scan: 'Scan a barcode',
  save: 'Save changes', cancel: 'Cancel this action', close: 'Close',
  view: 'View details', edit: 'Edit details', schedule: 'Schedule maintenance',
  'more filters': 'Show or hide additional filters',
};

// Existing explicit help wins. Text labels and the shared icon vocabulary cover
// legacy controls too, including buttons mounted later in dialogs or lazy routes.
export function getActionHelp(element) {
  const explicit = element.getAttribute('data-action-help') || element.getAttribute('title');
  const label = explicit || element.getAttribute('aria-label') ||
    element.textContent?.replace(/\s+/g, ' ').trim() || element.value;
  if (label?.toLowerCase() === 'clear' && element.closest('.reports-filter, .mantine-inventory-filters')) return 'Clear all filters';
  if (label) return helpers[label.toLowerCase()] || label;
  const icon = element.matches('[data-ui-icon], .fa') ? element : element.querySelector('[data-ui-icon], .fa');
  const name = icon?.getAttribute('data-ui-icon') ||
    [...(icon?.classList || [])].find(value => value.startsWith('fa-') && !/^fa-(?:\d+x|spin|fw)$/.test(value))?.slice(3);
  return iconHelp[name] || '';
}

export default function ActionTooltips({ enabled, portalId }) {
  const tooltipId = `action-help-${useId().replace(/:/g, '')}`;
  const tooltipRef = useRef(null);
  const [help, setHelp] = useState(null);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = null;
    let openTimer;
    let closeTimer;
    let originalTitle;
    let originalDescription;
    let originalLabel;
    const clear = () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (active) {
        if (originalTitle !== null && !active.hasAttribute('title')) active.setAttribute('title', originalTitle);
        if (active.getAttribute('aria-describedby')?.split(/\s+/).includes(tooltipId)) {
          if (originalDescription === null) active.removeAttribute('aria-describedby');
          else active.setAttribute('aria-describedby', originalDescription);
        }
        if (originalLabel === null && active.dataset.actionTooltipLabel === 'true') {
          active.removeAttribute('aria-label');
          delete active.dataset.actionTooltipLabel;
        }
      }
      active = null;
      setHelp(null);
      setPosition(null);
    };
    const controlFor = target => {
      if (!(target instanceof Element)) return null;
      const element = target.closest(actions);
      return element?.closest('.app--refined, .refined-mantine-portals, .site-refined-modal') ||
        (element?.closest('.modal') && document.body.classList.contains('app-site-refined')) ? element : null;
    };
    const addedLabels = new Map();
    const nameActions = root => {
      const elements = root.matches?.(actions) ? [root, ...root.querySelectorAll(actions)] : [...root.querySelectorAll(actions)];
      elements.forEach(element => {
        if (!controlFor(element) || element.hasAttribute('aria-label') || element.hasAttribute('aria-labelledby') || element.textContent?.trim()) return;
        const text = getActionHelp(element);
        if (text) { element.setAttribute('aria-label', text); addedLabels.set(element, text); }
      });
    };
    nameActions(document.body);
    const observer = new MutationObserver(records => {
      // Route changes must not retain detached controls for the life of the app.
      if (records.some(record => record.removedNodes.length)) {
        if (active && !active.isConnected) clear();
        addedLabels.forEach((label, element) => {
          if (!element.isConnected) {
            if (element.getAttribute('aria-label') === label) element.removeAttribute('aria-label');
            addedLabels.delete(element);
          }
        });
      }
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node instanceof Element) nameActions(node);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const show = (element, immediate) => {
      clearTimeout(closeTimer);
      if (active === element) return;
      clear();
      if (!element || element.hasAttribute('data-no-tooltip')) return;
      const text = getActionHelp(element);
      if (!text) return;
      active = element;
      originalTitle = element.getAttribute('title');
      originalDescription = element.getAttribute('aria-describedby');
      originalLabel = element.getAttribute('aria-label');
      // Suppress a duplicate native tooltip, restoring it on exit.
      element.removeAttribute('title');
      if (originalLabel === null && !element.textContent?.trim()) {
        element.setAttribute('aria-label', text);
        element.dataset.actionTooltipLabel = 'true';
      }
      openTimer = setTimeout(() => {
        if (!element.isConnected) { clear(); return; }
        element.setAttribute('aria-describedby', [originalDescription, tooltipId].filter(Boolean).join(' '));
        setHelp({ text, element });
      }, immediate ? 0 : 350);
    };
    const over = event => {
      if (event.pointerType === 'touch') return;
      if (event.target.closest?.('[data-action-tooltip]')) { clearTimeout(closeTimer); return; }
      show(controlFor(event.target), false);
    };
    const out = event => {
      if (active?.contains(event.relatedTarget) || event.relatedTarget?.closest?.('[data-action-tooltip]')) return;
      closeTimer = setTimeout(clear, 120);
    };
    const focus = event => show(controlFor(event.target), true);
    const blur = event => { if (!active?.contains(event.relatedTarget)) clear(); };
    const escape = event => { if (event.key === 'Escape') clear(); };
    document.addEventListener('pointerover', over);
    document.addEventListener('pointerout', out);
    document.addEventListener('focusin', focus);
    document.addEventListener('focusout', blur);
    document.addEventListener('click', clear, true);
    document.addEventListener('keydown', escape);
    document.addEventListener('scroll', clear, true);
    window.addEventListener('resize', clear);
    return () => {
      observer.disconnect();
      clear();
      addedLabels.forEach((label, element) => { if (element.getAttribute('aria-label') === label) element.removeAttribute('aria-label'); });
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerout', out);
      document.removeEventListener('focusin', focus);
      document.removeEventListener('focusout', blur);
      document.removeEventListener('click', clear, true);
      document.removeEventListener('keydown', escape);
      document.removeEventListener('scroll', clear, true);
      window.removeEventListener('resize', clear);
    };
  }, [enabled, tooltipId]);

  useLayoutEffect(() => {
    if (!help || !tooltipRef.current) return;
    const target = help.element.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();
    const below = target.top < tooltip.height + 16;
    setPosition({
      left: Math.max(12, Math.min(target.left + target.width / 2 - tooltip.width / 2, window.innerWidth - tooltip.width - 12)),
      top: Math.max(8, Math.min(below ? target.bottom + 8 : target.top - tooltip.height - 8, window.innerHeight - tooltip.height - 8)),
    });
  }, [help]);

  const host = enabled && document.getElementById(portalId);
  if (!host || !help) return null;
  return createPortal(<div ref={tooltipRef} id={tooltipId} role="tooltip" data-action-tooltip
    className="refined-action-tooltip" style={{ ...position, visibility: position ? 'visible' : 'hidden' }}>
    {help.text}
  </div>, host);
}
